import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { FormContext } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'

import { createNodeSidepanelSchema } from './nodeSidepanelSchema'

export const createConditionalFormSchema = (formContext: FormContext) => {
  return createNodeSidepanelSchema(formContext).extend({
    nodeType: z.literal('condition'),
    nodeData: z.undefined(),
    comparisons: z
      .array(
        z.object({
          label: z
            .string()
            .min(1, { message: i18n.t('workflows.sidepanel.condition.validation.label_required') }),
          priority: z.number().min(1, {
            message: i18n.t('workflows.sidepanel.condition.validation.priority_min_value'),
          }),
          expression: z.object({
            root: z.object({
              operator: z.enum(['and']),
              expressions: z.array(
                z
                  .object({
                    left: z.object({
                      name: z.string(),
                      source: z.enum(['input', 'dynamic_input']),
                    }), //when we add the variable type, we'll be able to restrict the operator options
                    operator: z.enum([
                      'eq',
                      'neq',
                      'gt',
                      'gte',
                      'lt',
                      'lte',
                      'one_of',
                      'not_one_of',
                      'contains',
                      'not_contains',
                    ]),
                    right: z.object({
                      value: z.string(),
                      type: z.enum([
                        'boolean',
                        'float',
                        'integer',
                        'nil',
                        'string',
                        'integer_array',
                        'string_array',
                      ]),
                    }),
                  })
                  .refine(
                    (data) => data.left.name,
                    () => ({
                      message: i18n.t('workflows.sidepanel.condition.validation.select_required'),
                      path: ['left', 'name'],
                    })
                  )
                  .refine(
                    (data) => data.operator,
                    () => ({
                      message: i18n.t('workflows.sidepanel.condition.validation.operator_required'),
                      path: ['operator'],
                    })
                  )
                  .refine(
                    (data) => {
                      // For array types, empty array '[]' is considered empty
                      if (data.right.type === 'integer_array' || data.right.type === 'string_array') {
                        return data.right.value.length > 0 && data.right.value !== '[]'
                      }
                      return data.right.value.length > 0
                    },
                    (errorData) => {
                      const variableConfig = formContext.variablesConfig[errorData.left.name]
                      const displayName = variableConfig?.label ?? errorData.left.name

                      return {
                        message: i18n.t('workflows.sidepanel.condition.validation.value_required', {
                          name: displayName,
                        }),
                        path: ['right', 'value'],
                      }
                    }
                  )
              ),
            }),
          }),
        })
      )
      .min(1, {
        message: i18n.t(
          'workflows.sidepanel.condition.validation.at_least_one_condition_path_required'
        ),
      }),
  })
}

export type ConditionalFormSchema = z.infer<ReturnType<typeof createConditionalFormSchema>>
